import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import PageHeader from '../../common/PageHeader'
import Section from '../../common/Section'
import Alert from '../../common/Alert'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import PaymentsIcon from '@mui/icons-material/Payments'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import InventoryIcon from '@mui/icons-material/Inventory'
import ReceiptIcon from '@mui/icons-material/Receipt'
import { useLocale } from '../../../i18n/useLocale'

export default function Overview() {
  const { content, payment, brand } = useLocale()
  const mobileMoney = payment.mobileMoney?.name || 'mobile money'

  const features = [
    {
      icon: <InventoryIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Parcel Creation',
      description: 'Create parcels via REST API or batch file upload (CSV/Excel). Full validation and error handling.',
    },
    {
      icon: <QrCodeScannerIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Label Generation',
      description: 'Generate shipping labels in PDF or ZPL format for thermal printers. Batch support included.',
    },
    {
      icon: <LocalShippingIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Parcel Tracking',
      description: 'Real-time parcel status tracking. Query single or batch parcels by barcode.',
    },
    {
      icon: <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Pricing',
      description: 'Get real-time pricing based on parcel size, service type, and partner tier.',
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Billing',
      description: content.overview.billing,
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'APM Finder Widget',
      description: 'Embeddable map widget for customers to select pickup/dropoff locations.',
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Partner Integration Overview"
        description={`Welcome to the ${brand.name} Partner Integration Portal. This documentation will help you integrate your e-commerce platform with our parcel delivery network.`}
        breadcrumbs={[
          { label: 'Getting Started', path: '/getting-started' },
          { label: 'Overview' },
        ]}
      />

      <Alert type="info" title="For Enterprise Partners">
        This documentation is designed for large e-commerce platforms and webshops integrating with {brand.name}
        {' '}via our REST APIs. For smaller customers, we also offer a Customer Portal with a web interface.
      </Alert>

      <Section title={`What is ${brand.name}?`} divider={false}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {content.overview.networkDescription}
        </Typography>
        <Typography variant="body1">
          As a partner, you can integrate your order management system directly with our APIs to:
        </Typography>
        <Box component="ul" sx={{ mt: 2, pl: 3 }}>
          <li>Automatically create parcels when orders are placed</li>
          <li>Generate shipping labels for your warehouse</li>
          <li>Track parcel status in real-time</li>
          <li>Let customers choose their preferred pickup location</li>
          <li>Handle payments for Cash-on-Delivery orders</li>
        </Box>
      </Section>

      <Section title="Integration Capabilities">
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {feature.icon}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Section title="Integration Architecture">
        <Typography variant="body1" sx={{ mb: 2 }}>
          All API requests are made through our API Gateway, which handles authentication and routes
          requests to the appropriate backend service.
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3,
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflow: 'auto',
          }}
        >
          <pre style={{ margin: 0 }}>
{`┌─────────────────┐         ┌─────────────────┐
│   Your System   │ ──────► │   API Gateway   │
│   (Webshop)     │  HTTPS  │   (${brand.name})${brand.name.length < 9 ? ' '.repeat(9 - brand.name.length) : ''}│
└─────────────────┘         └────────┬────────┘
                                     │
        ┌────────────────────────────┼────────────────────────────┐
        │                            │                            │
        ▼                            ▼                            ▼
┌───────────────┐          ┌───────────────┐          ┌───────────────┐
│  Parcel API   │          │   Label API   │          │  Payment API  │
│               │          │               │          │               │
└───────────────┘          └───────────────┘          └───────────────┘`}
          </pre>
        </Box>
      </Section>

      <Section title="Partner Types">
        <Typography variant="body1" sx={{ mb: 2 }}>
          {brand.name} supports different partner types with varying pricing and features:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary.main">B2C (Business-to-Consumer)</Typography>
              <Typography variant="body2" color="text.secondary">
                For e-commerce businesses shipping to consumers. Supports post-paid billing,
                custom label branding, and volume discounts.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary.main">C2C (Consumer-to-Consumer)</Typography>
              <Typography variant="body2" color="text.secondary">
                For marketplace platforms where individuals send parcels to each other.
                Pre-paid model{payment.mobileMoney ? ` with ${mobileMoney} integration` : ''}.
              </Typography>
            </CardContent>
          </Card>
          {/* <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary.main">MK2C (Marketplace-to-Consumer)</Typography>
              <Typography variant="body2" color="text.secondary">
                Hybrid model for marketplaces with mixed seller types.
                Flexible payment options per transaction.
              </Typography>
            </CardContent>
          </Card> */}
        </Box>
      </Section>

      <Section title="Next Steps">
        <Typography variant="body1">
          Ready to get started? Follow these steps:
        </Typography>
        <Box component="ol" sx={{ mt: 2, pl: 3, '& li': { mb: 1.5 } }}>
          <li>
            <strong>Set up authentication</strong> - Learn about API key authentication in the{' '}
            <a href="/getting-started/authentication">Authentication</a> section.
          </li>
          <li>
            <strong>Make your first API call</strong> - Follow our{' '}
            <a href="/getting-started/quick-start">Quick Start</a> guide to create your first parcel.
          </li>
          <li>
            <strong>Explore the APIs</strong> - Dive into the{' '}
            <a href="/guides/parcel-creation">Integration Guides</a> for detailed implementation examples.
          </li>
          <li>
            <strong>Add the APM Finder widget</strong> - Let customers choose pickup locations with our{' '}
            <a href="/widgets/apm-finder">embeddable widget</a>.
          </li>
        </Box>
      </Section>
    </Box>
  )
}
